import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type * as React from 'react';
import { match } from 'ts-pattern';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative grid mobile:w-112 w-66 w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        info: 'bg-card text-card-foreground',
        error:
          'bg-destructive-foreground text-card-foreground *:data-[slot=alert-title]:text-error [&>svg]:text-error',
        success:
          'bg-success-foreground text-card-foreground *:data-[slot=alert-title]:text-success [&>svg]:text-success',
        warn: 'bg-warning-foreground text-card-foreground *:data-[slot=alert-title]:text-warning [&>svg]:text-warning',
        default: 'bg-card text-card-foreground',
        destructive:
          'bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type AlertProps = React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants>;

function Alert({ className, variant, children, ...props }: AlertProps) {
  const DefaultIcon = match(variant)
    .with('error', () => AlertTriangle)
    .with('success', () => CheckCircle2)
    .with('warn', () => AlertCircle)
    .with('info', () => Info)
    .otherwise(() => null);

  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    >
      {DefaultIcon && <DefaultIcon />}
      {children}
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight',
        className,
      )}
      data-slot="alert-title"
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'col-start-2 grid justify-items-start gap-1 text-muted-foreground text-sm [&_p]:leading-relaxed',
        className,
      )}
      data-slot="alert-description"
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle };
